<template>
    <div class="guest-signup">
        <section v-if="!guestCheckoutEnbaled" class="text-center">
            <h2 v-t="'guestSignup.title'" class="guest-title"></h2>
            <div v-t="'guestSignup.subtitle'" class="guest-subtitle"></div>
        </section>
        <section v-else class="text-center">
            <h2 v-t="'guestSignup.guestCheckoutTitle'" class="guest-title"></h2>
            <div v-t="'guestSignup.guestCheckoutSubtitle'" class="guest-subtitle"></div>
        </section>
        <section v-if="!guestCheckoutEnbaled" class="signin-link text-center">
            <p>{{ $t('guestSignup.alreadyCustomer') }} <a :href="loginUrl">{{ $t('guestSignup.signIn') }}</a></p>
        </section>
        <section>
            <v-form ref="form" v-model="valid" lazy-validation>
                <v-radio-group
                    v-if="enableCreateGuest && !guestCheckoutEnbaled"
                    v-model="accountType"
                    column
                    :mandatory="true"
                >
                    <v-radio color="#272525" :label="$t('guestSignup.checkoutAsAGuest')" value="guest" />
                    <v-radio color="#272525" value="create" class="has-sub-message">
                        <template #label>
                            <span>{{ $t('guestSignup.createAnAccount') }} </span>
                            <span class="radio-sub-message">{{ $t('guestSignup.saveOrderHistory') }}</span>
                        </template>
                    </v-radio>
                </v-radio-group>

                <SignupForm
                    v-if="accountType === 'create'"
                    :api-client-id="apiClientId"
                    :api-client-service-env="apiClientServiceEnv"
                    :invite-id="inviteId"
                    :language="language"
                    :market="market"
                    :origin="origin"
                    signup-type="CUSTOMER"
                    :sponsor-id="sponsorId"
                    :submit-button-html="$t('guestSignup.continueToCheckout')"
                    @signup-completed="onSignupCompleted"
                />
                <GuestSignupForm
                    v-else
                    :sponsor-id="sponsorId"
                    :invite-id="inviteId"
                    :language="language"
                    :market="market"
                    :origin="origin"
                    :is-cold-guest-signup="guestCheckoutEnbaled"
                    :submit-button-html="$t('guestSignup.continueAsGuest')"
                    @guest-signup-completed="onSignupCompleted"
                />
            </v-form>

            <p v-if="showError" class="unknown-error v-messages error--text">
                {{ $t('guestSignup.unknownError') }}
            </p>
        </section>
    </div>
</template>

<script>
import {VForm, VRadioGroup, VRadio} from 'vuetify/lib';
import {SignupForm} from '@nuskin/account-components';
import {GuestSignupForm} from '@nuskin/account-components';
import appStage, {clientId} from '~/config/appStage';
import {CartService} from '@nuskin/ns-shop';
import {
    AuthenticationService,
    SignupService,
    UserService
} from '@nuskin/ns-account';
import {util, storage} from '@nuskin/ns-util';
import ensureSignupSession from '~/utils/ensureSignupSession.js';
import downTimeCheck from '~/mixin/downTimeCheck';

const LOGIN_URL_TEMPLATE =
'/content/login/corporate/{language}.html?destination={destination}&cancel={cancel}&market={market}';

export default {
    components: {
        VForm,
        VRadioGroup,
        VRadio,
        SignupForm,
        GuestSignupForm
    },
    mixins: [downTimeCheck],
    data() {
        const locale = this.$route.params.locale;
        const [language, market] = locale.split('_');

        // Go to checkout, only use mysite url for storefront context
        const shoppingContext = storage.getItem(storage.keys.SHOPPING_CONTEXT);
        const isMySite = shoppingContext?.context === 'storefront';

        const afterLoginCookie = storage.getItem(storage.keys.AFTER_LOGIN_URL);
        const destination = afterLoginCookie || CartService.getCheckoutPage({isMySite});

        const loginUrl = LOGIN_URL_TEMPLATE.replace('{language}', language)
            .replace('{market}', market)
            .replace('{destination}', encodeURIComponent(destination))
            .replace('{cancel}', encodeURIComponent(document.referrer));

        let accountType = 'guest';
        let enableCreateGuest = true;

        if (CartService.containsAdrItems()) {
            accountType = 'create';
            enableCreateGuest = false;
        }

        /* istanbul ignore next */
        const apiClientServiceEnv = (process.env.mockApi === 'true') ?
            'mock' :
            appStage.service;

        return {
            isMySite,
            destination,
            loginUrl,
            accountType,
            enableCreateGuest,

            valid: false,
            showError: false,
            processing: false,
            formErrorExists: false,
            apiClientId: clientId,
            apiClientServiceEnv,
            inviteId: this.$route.query.inviteId || '',
            language,
            market,
            sponsorId: this.$route.query.sponsor,
            sponsor: ''
        };
    },
    computed: {
        guestCheckoutEnbaled() {
            return this.$route.query.inviteId == 'coldGuestSignup';
        },
        origin() {
            if (this.guestCheckoutEnbaled) {
                return 'WBC';
            } else {
                return SignupService.getSignupChannel({isGuest: this.accountType === 'guest'});
            }
        },
        hideLoginSignUp() {
            return util.isPersonalOffer();
        }
    },
    async mounted() {
        await this.checkDownTime();
        if (this.hideLoginSignUp) this.loginUrl = this.loginUrl + '&hideSignup=true';

        storage.removeItem(storage.keys.AFTER_LOGIN_URL);
        if (AuthenticationService.isLoggedIn()) {
            if (UserService.getUser().isGuest) {
                CartService.goToCheckoutPage({isMySite: this.isMySite, route: 'orderSummary'});
            } else {
                window.location = this.destination;
            }
        }
        if (!this.sponsorId) {
            window.location = this.loginUrl;
        }
        ensureSignupSession({
            entrypoint: 'guest-checkout',
            market: this.market,
            language: this.language,
            signupType: 'guest'
        });
    },
    methods: {
        onSignupCompleted() {
            CartService.goToCheckoutPage({isMySite: this.isMySite, route: 'orderSummary'});
        }
    }
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/buttons";
@use "~@nuskin/ns-core-styles/src/scss/other";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";
@use "~@nuskin/ns-core-styles/src/scss/globalVariables";
section {
  &.text-center {
    text-align: center;
  }
margin-bottom: 30px;
}
.guest-title {
    font-weight: 100;
    font-size: 40px;
    line-height: 43px;
    text-transform: none;
    margin-bottom: 10px;
  }
.signin-link {
    border-bottom: 1px solid map-get(colors.$baseColors, ash);
    padding: 20px 0;
    a {
      color: map-get(colors.$baseColors, shells);
      font-weight: bold;
    }
  }

  p {
    @include global.p-medium();
    margin-bottom: 0;
  }
.accent--text {
    caret-color: inherit;
    color: inherit;
  }
  ::v-deep{
    .has-sub-message {
      .v-label {
        flex-flow: column wrap !important;
        justify-content: center;
        align-items: flex-start;
      }
      .radio-sub-message {
        padding: 10px 0 0;
        font-size: 14px;
      }

    }
  }
</style>
